import { Component, ElementRef, HostBinding, OnDestroy, OnInit, Renderer2, ViewChild, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute } from '@angular/router';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import {
  BehaviorSubject,
  combineLatest,
  NEVER,
  Observable,
  Subscription,
  zip,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { LoaderColor, LoaderTheme } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { AppState, ConfigService, NativeBridgeService } from '@fcom/core';
import { unsubscribe } from '@fcom/core/utils';
import { WindowRef } from '@fcom/core/providers/window';
import { CmsDataService } from '@fcom/core/services';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors/login.selector';
import { ElementActions, ElementTypes, GaContext, MediaQueryService } from '@fcom/common';
import { finShare } from '@fcom/rx';
import { GtmService } from '@fcom/common/gtm';

import { ChatStatus, HelpButtonStatus, LiveagentChatFields, PrechatFields } from '../../interfaces';
import { SalesforceChatConfig, SalesforceChatConfigService } from '../../services/salesforce-chat-config.service';
import { SalesforceChatService } from '../../services/salesforce-chat.service';
import { AgentforceChatService } from '../../services/agentforce-chat.service';

// The class of the div for "All agents are busy or offline, please try again later" message
// that we replace with our custom HTML
const OFFLINE_DIALOG_CLASS_NAME = 'embeddedServiceLiveAgentStateOfflineSupportDefaultUI';
const MODAL_OPEN_CLASS_NAME = 'modal-open';

@Component({
  selector: 'fin-chat',
  templateUrl: 'chat.component.html',
  styleUrls: ['chat.component.scss'],
  standalone: false,
})
export class ChatComponent implements OnInit, OnDestroy {
  private cmsDataService = inject(CmsDataService);
  private languageService = inject(LanguageService);
  private salesforceChatConfigService = inject(SalesforceChatConfigService);
  private salesforceChatService = inject(SalesforceChatService);
  private store$ = inject<Store<AppState>>(Store);
  private windowRef = inject(WindowRef);
  private mediaQueryService = inject(MediaQueryService);
  private route = inject(ActivatedRoute);
  private readonly renderer = inject(Renderer2);
  private document = inject<Document>(DOCUMENT);
  private gtmService = inject(GtmService);
  private configService = inject(ConfigService);
  private afChatService = inject(AgentforceChatService);
  private nativeBridgeService = inject(NativeBridgeService);

  private subscription = new Subscription();
  private allAgentsAreBusyOrOfflineSubscription = new Subscription();

  readonly HelpButtonStatus = HelpButtonStatus;
  readonly LoaderTheme = LoaderTheme;
  readonly LoaderColor = LoaderColor;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  chatStatus: ChatStatus = ChatStatus.INITIAL;
  embeddedSvc: {
    settings: Record<string, unknown>;
    init: Function;
    bootstrapEmbeddedService: Function;
    addEventHandler: Function;
  };
  chatButtonState$: BehaviorSubject<HelpButtonStatus>;
  disableChat$: BehaviorSubject<boolean>;
  openChatTrigger$: BehaviorSubject<HelpButtonStatus> = new BehaviorSubject(HelpButtonStatus.VISIBLE);
  dirty$: Observable<boolean>;
  agentforceLocaleAllowed = true;

  @ViewChild('target', { static: true }) target: ElementRef;

  // This is to set the mask all chat content in Hotjar sessions
  @HostBinding('attr.data-hj-suppress') maskHotJar = '';

  ngOnInit(): void {
    // Disable chat in app
    if (this.nativeBridgeService.isInsideNativeWebView) {
      return;
    }
    this.chatButtonState$ = this.afChatService.chatButtonState$;
    this.disableChat$ = this.afChatService.disableChat$;
    this.agentforceLocaleAllowed = this.configService.cfg.enableSplitChat
      ? this.afChatService.checkAllowedLocales(this.languageService.langValue)
      : false;

    // Check if feature flag is not enabled
    // OR load the old chat if the new chat is enabled and the site is not in English
    if (!this.configService.cfg.enableSplitChat || !this.agentforceLocaleAllowed) {
      this.initChat(this.salesforceChatConfigService.scriptSrcURL);

      this.subscription.add(
        this.salesforceChatService.openChat$
          .pipe(filter(() => !!this.embeddedSvc))
          .subscribe((fields: PrechatFields) => {
            this.embeddedSvc.settings.prepopulatedPrechatFields = {
              FirstName: fields.firstName,
              LastName: fields.lastName,
              Email: fields.email,
              Subject: fields.subject,
              Booking_Reference__c: fields.bookingReference,
            };
            this.startChat();
          })
      );
    }

    this.subscription.add(
      this.languageService.locale.subscribe((l): void => {
        if (l.includes('RU')) {
          this.afChatService.disableChat$.next(true);
          this.afChatService.chatButtonState$.next(HelpButtonStatus.HIDDEN);
          return;
        }

        if (this.disableChat$.value) {
          this.afChatService.chatButtonState$.next(HelpButtonStatus.VISIBLE);
          this.afChatService.disableChat$.next(false);
        }
      })
    );

    if (this.agentforceLocaleAllowed) {
      this.subscription.add(
        this.afChatService.openLegacyChat$.subscribe((fields: LiveagentChatFields): void => {
          this.afChatService.liveAgentChat(fields, this.target);
        })
      );

      // refresh the page to so that legacy chat is booted up on service queue change
      this.subscription.add(
        this.languageService.lang.subscribe((langValue): void => {
          if (!this.afChatService.checkAllowedLocales(langValue)) {
            window.location.reload();
          }

          this.afChatService.setAgentforceLanguage(langValue);
        })
      );

      this.subscription.add(
        combineLatest([
          this.afChatService.openWebMessagingChat$,
          this.afChatService.messagingForWebTriggered$,
        ]).subscribe(([fields, hasBeenTriggered]): void => {
          if (hasBeenTriggered) {
            this.afChatService.startWebMessagingChat(fields);
            return;
          }

          this.afChatService.webMessagingChat(fields, this.target);
        })
      );
    }

    // Mimic the same behavior from the old SF chat button when chat button is
    // clicked once then it is visible always and everywhere until refresh of the page
    this.dirty$ = this.afChatService.chatButtonState$.pipe(
      filter((helpButtonStatus) => helpButtonStatus !== HelpButtonStatus.VISIBLE),
      map(() => true),
      take(1),
      finShare()
    );

    this.subscription.add(
      // prevent from scrolling page when chat and prechat screens are open in mobile
      combineLatest([this.mediaQueryService.isBreakpoint$('mobile'), this.afChatService.chatButtonState$])
        .pipe(
          map(([isMobile, openChatTrigger]) => isMobile && openChatTrigger !== HelpButtonStatus.VISIBLE),
          distinctUntilChanged()
        )
        .subscribe((shouldSetModalOpen) => {
          if (shouldSetModalOpen) {
            this.renderer.addClass(this.document.body, MODAL_OPEN_CLASS_NAME);
          } else {
            this.renderer.removeClass(this.document.body, MODAL_OPEN_CLASS_NAME);
          }
        })
    );

    this.subscription.add(
      this.route.queryParams
        .pipe(
          take(1),
          filter((params) => params.openChat?.toLowerCase() === 'true')
        )
        .subscribe(() => {
          this.chatTrigger(HelpButtonStatus.CLICKED);
        })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
    unsubscribe(this.allAgentsAreBusyOrOfflineSubscription);
  }

  chat(): void {
    if (!this.embeddedSvc) {
      // Salesforce script failed to load/initialize, we don't have base settings, so unable to boot.
      this.chatStatus = ChatStatus.ERROR;
      return;
    }
    this.chatStatus = ChatStatus.LOADING;
    const config: SalesforceChatConfig = this.salesforceChatConfigService.config;

    // HACK. Salesforce Chat is unable to reboot with a new serviceQueue (language, buttonId) after
    // it has been booted, so we have to reload the whole page whenever language changes.
    this.subscription.add(
      this.languageService.lang.subscribe(() => {
        if (this.embeddedSvc.settings.buttonId) {
          window.location.reload();
        }
      })
    );

    this.subscription.add(
      combineLatest([
        zip(this.languageService.lang, this.languageService.localizations),
        this.store$.pipe(profileOrUndefinedIfNotLoggedIn()),
      ]).subscribe(([[langValue, translations], profile]) => {
        // Call config.setLanguage to set configuration based on language,
        // serviceQueueId is set by that if Chat is enabled for that language
        config.setLanguage(langValue);

        if (config.serviceQueueId) {
          config.setTargetElement(this.target.nativeElement);

          // If the user is logged in, pass information to pre-chat form
          if (profile) {
            config.settings.prepopulatedPrechatFields = {
              FirstName: profile.firstname,
              LastName: profile.lastname,
              Email: profile.email,
              Subject: '',
              Finnair_Plus_Number__c: profile.memberNumber,
              Case_Member_Tier__c: profile.tier,
            };
          }
          if (translations.chat) {
            config.settings.defaultMinimizedText = translations.chat.defaultMinimizedText;
            config.settings.disabledMinimizedText = translations.chat.defaultMinimizedText;
            config.settings.offlineSupportMinimizedText = translations.chat.defaultMinimizedText;
          } else {
            delete config.settings.disabledMinimizedText;
            delete config.settings.defaultMinimizedText;
            delete config.settings.offlineSupportMinimizedText;
          }

          // when chat is fully expanded and visible
          this.embeddedSvc.addEventHandler('afterMaximize', () => {
            this.afChatService.chatButtonState$.next(HelpButtonStatus.HIDDEN);
          });

          // when chat is closed (X is clicked)
          this.embeddedSvc.addEventHandler('afterDestroy', () => {
            this.afChatService.chatButtonState$.next(HelpButtonStatus.VISIBLE);
          });

          //this might be where I was going wrong! Needs to happen earlier?
          this.embeddedSvc.settings = { ...this.embeddedSvc.settings, ...config.settings };
          this.embeddedSvc.init(...config.initParams);

          this.chatStatus = ChatStatus.COMPLETE;

          // TODO: add masking class via javascript?
        }
      })
    );
  }
  /*
    Ask our service to load SalesForce's external .js file. That .js file adds an object to window.embedded_svc.
    Store that reference and then boot up the chat with this.chat()
  */
  initChat(scriptSrcURL: string): void {
    this.salesforceChatService
      .load(scriptSrcURL)
      .then(() => {
        this.embeddedSvc = this.windowRef.nativeWindow['embedded_svc'];
        this.chat();
      })
      .catch((_error) => {
        this.chatStatus = ChatStatus.ERROR;
      });
  }

  /*
   * Listens to events of MutationObserver (finMutationObserver) and replaces the basic unlocalized
   * "All agents are offline or busy" message with a Finnair custom message that contains chat's service
   * hours and information that the US site's chat is open 24/7
   */
  onDomChange($event: any): void {
    const addedNodes: NodeList = $event.addedNodes;
    addedNodes.forEach((node: Element) => {
      if (node.classList?.contains(OFFLINE_DIALOG_CLASS_NAME)) {
        // The "All agents are busy or offline" message appeared, replace it
        // with a HTML fragment from CMS
        this.subscription.remove(this.allAgentsAreBusyOrOfflineSubscription);
        this.allAgentsAreBusyOrOfflineSubscription = this.languageService
          .translate('fragments.chatAgentsBusyOrOffline.url')
          .pipe(
            filter((v) => !!v),
            switchMap((url) => (!url ? NEVER : this.cmsDataService.getFragmentJson(url))),
            filter((s: any) => s.detailText),
            map((s: any) => s.detailText),
            take(1)
          )
          .subscribe((detailTextHtml: string) => {
            node.innerHTML = detailTextHtml;
          });
        this.subscription.add(this.allAgentsAreBusyOrOfflineSubscription);
      }
    });
  }

  chatTrigger(shouldOpen: HelpButtonStatus): void {
    this.afChatService.chatButtonState$.next(shouldOpen);

    if (shouldOpen === HelpButtonStatus.CLICKED) {
      this.trackElement('open-chat-button');
    }
  }

  startChat(): void {
    this.embeddedSvc.bootstrapEmbeddedService();
    this.afChatService.chatButtonState$.next(HelpButtonStatus.CHAT);
    this.trackElement('start-chatting-chat-button');
  }

  private trackElement(label: string): void {
    this.gtmService.trackElement(`${label}`, GaContext.CHAT, ElementTypes.BUTTON, undefined, ElementActions.CLICK);
  }
}
